import { CLASSIC_ANALYSIS_PAGES } from "@/features/analytics-classic/registry";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { findAnalysis } from "@/features/analytics/analyses";
import {
  FlaskConical,
  Wrench,
  Calculator,
  FileText,
  BarChart,
  type LucideIcon,
} from "lucide-react";
import { AnalyticCategoryEnum } from "@brewbase/db/types";

type Analytic = RouterOutputs["analytics"]["list"][number];

const ANALYTIC_ICONS: Record<string, LucideIcon> = {
  FlaskConical,
  Wrench,
  Calculator,
  FileText,
  BarChart,
};

const CATEGORY_LABELS: Record<AnalyticCategoryEnum, string> = {
  [AnalyticCategoryEnum.analysis]: "Analysis",
  [AnalyticCategoryEnum.report]: "Reports",
  [AnalyticCategoryEnum.utility]: "Utilities",
};

const CATEGORY_ORDER: AnalyticCategoryEnum[] = [
  AnalyticCategoryEnum.analysis,
  AnalyticCategoryEnum.report,
  AnalyticCategoryEnum.utility,
];

/** The pre-redesign analytics index, kept while operators switch over. */
export const Route = createFileRoute("/analytics/classic/")({
  component: AnalyticsIndex,
});

function AnalyticsIndex() {
  const { data: allAnalytics } = trpc.analytics.list.useQuery();
  // Only analyses with a classic page; the rest moved into the new view.
  const analytics = allAnalytics?.filter((a) => {
    const analysis = findAnalysis(a.slug);
    return analysis !== undefined && CLASSIC_ANALYSIS_PAGES[analysis.slug] !== undefined;
  });

  const grouped = useMemo(() => groupByCategory(analytics ?? []), [analytics]);

  return (
    <div className="container mx-auto flex h-full w-full max-w-[120rem] flex-col gap-6 overflow-auto px-4 py-6">
      <div>
        <h1 className="text-base font-medium">Analytics (classic)</h1>
        <p className="text-sm text-muted-foreground">
          Data analysis, reporting, and lab workflow utilities.
        </p>
      </div>

      {CATEGORY_ORDER.map((category) => {
        const items = grouped[category];
        if (!items || items.length === 0) return null;
        return (
          <section key={category} className="flex flex-col gap-3">
            <h2 className="text-sm font-medium text-muted-foreground">
              {CATEGORY_LABELS[category]}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((analytic) => (
                <AnalyticCard key={analytic.id} analytic={analytic} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function AnalyticCard({ analytic }: { analytic: Analytic }) {
  const Icon = (analytic.icon && ANALYTIC_ICONS[analytic.icon]) || Wrench;
  return (
    <Link
      to="/analytics/classic/$slug"
      params={{ slug: analytic.slug }}
      className="block hover:bg-olive-3 transition-colors"
    >
      <Card className="h-full">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <CardTitle className="text-base">{analytic.name}</CardTitle>
          </div>
          {analytic.description && (
            <CardDescription className="text-sm">
              {analytic.description}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent />
      </Card>
    </Link>
  );
}

function groupByCategory(
  analytics: Analytic[],
): Partial<Record<AnalyticCategoryEnum, Analytic[]>> {
  const out: Partial<Record<AnalyticCategoryEnum, Analytic[]>> = {};
  for (const analytic of analytics) {
    const bucket = out[analytic.category] ?? [];
    bucket.push(analytic);
    out[analytic.category] = bucket;
  }
  return out;
}
